import { useState, useMemo, useEffect } from 'react';
import { List, useListContext, CreateButton, TopToolbar, useRedirect } from 'react-admin';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, InputAdornment, IconButton, Stack, CircularProgress,
} from '@mui/material';
import SettingsIcon  from '@mui/icons-material/Settings';
import SearchIcon    from '@mui/icons-material/Search';
import AddIcon      from '@mui/icons-material/Add';
import EditIcon     from '@mui/icons-material/Edit';
import DeleteIcon   from '@mui/icons-material/Delete';
import SaveIcon     from '@mui/icons-material/Save';
import CloseIcon    from '@mui/icons-material/Close';
import { tbl, btn, NAVY, inputSx, clickableA11y } from '../styles/ds';
import authFetch from '../utils/authFetch';
import { useFitCount } from '../hooks/useFitCount';
import FitPagination from '../components/FitPagination';

// ── Role color map ─────────────────────────────────────────────────────────────
const roleColor = name => {
  const map = {
    'תוגג':       { bg: '#CCE5FF', color: '#004085' },
    'מגיה':       { bg: '#D4EDDA', color: '#155724' },
    'סוגר בתים':  { bg: '#FFF3CD', color: '#856404' },
    'כורך':       { bg: '#F8D7DA', color: '#721C24' },
    'בודק':       { bg: '#D1ECF1', color: '#0C5460' },
  };
  return map[name] || { bg: '#E2E3E5', color: '#383D41' };
};

// ── Manage Roles Dialog ────────────────────────────────────────────────────────
const ManageRolesDialog = ({ open, onClose }) => {
  const [roles, setRoles]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [newName, setNewName]   = useState('');
  const [editId, setEditId]     = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const loadRoles = () => {
    setLoading(true);
    authFetch('/worker_roles?_start=0&_end=100&_sort=id&_order=ASC')
      .then(r => r.json())
      .then(d => { setRoles(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  // Load roles when dialog opens
  if (open && !loading && roles.length === 0) loadRoles();

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true); setError('');
    try {
      const res = await authFetch('/worker_roles', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (!res.ok) { const b = await res.json(); throw new Error(b.error); }
      setNewName('');
      loadRoles();
    } catch (e) { setError(e.message || 'שגיאה'); }
    finally { setSaving(false); }
  };

  const handleEdit = async (id) => {
    if (!editName.trim()) return;
    setSaving(true); setError('');
    try {
      const res = await authFetch(`/worker_roles/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName.trim() }),
      });
      if (!res.ok) { const b = await res.json(); throw new Error(b.error); }
      setEditId(null); setEditName('');
      loadRoles();
    } catch (e) { setError(e.message || 'שגיאה'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('למחוק תפקיד זה?')) return;
    setError('');
    try {
      const res = await authFetch(`/worker_roles/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        setError(b.error || 'שגיאה במחיקה');
        return;
      }
      loadRoles();
    } catch (_) { setError('שגיאה במחיקה'); }
  };

  return (
    <Dialog open={open} onClose={onClose} dir="rtl" maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: NAVY }}>
        ניהול תפקידים
        <IconButton size="small" onClick={onClose} aria-label="סגור"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Add new role */}
        <Stack className="crm-filter-row" direction="row" spacing={1} mb={2}>
          <TextField
            size="small" placeholder="תפקיד חדש..." value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            sx={{ ...inputSx, flex: 1 }}
          />
          <Button variant="contained" size="small" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
            onClick={handleAdd} disabled={!newName.trim() || saving} sx={btn.primary}>
            הוסף
          </Button>
        </Stack>

        {error && <Typography color="error" fontSize={12} mb={1}>{error}</Typography>}

        {/* Roles list */}
        {loading ? <CircularProgress size={20} /> : (
          <Stack spacing={1}>
            {roles.map(role => (
              <Box key={role.id} sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1.5, px: 1.5, py: 0.8,
                backgroundColor: editId === role.id ? 'rgba(13,29,62,0.03)' : '#fff',
              }}>
                {editId === role.id ? (
                  <>
                    <TextField size="small" value={editName} onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleEdit(role.id)}
                      aria-label="שם תפקיד"
                      sx={{ ...inputSx, flex: 1 }} autoFocus />
                    <IconButton size="small" onClick={() => handleEdit(role.id)} aria-label="שמירת תפקיד" sx={{ color: NAVY }}>
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => { setEditId(null); setEditName(''); }} aria-label="ביטול עריכה">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Chip label={role.name} size="small"
                      sx={{ ...roleColor(role.name), fontWeight: 600, fontSize: 12, flex: 1, justifyContent: 'flex-start' }} />
                    <IconButton size="small" onClick={() => { setEditId(role.id); setEditName(role.name); }} aria-label="עריכת תפקיד" sx={{ color: '#555' }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(role.id)} aria-label="מחיקת תפקיד" sx={{ color: '#c62828' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            ))}
            {roles.length === 0 && <Typography fontSize={13} color="text.secondary" textAlign="center">אין תפקידים עדיין</Typography>}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>סגור</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Toolbar ────────────────────────────────────────────────────────────────────
const Toolbar = ({ onManageRoles }) => (
  <TopToolbar>
    <Button variant="outlined" size="small" startIcon={<SettingsIcon />} onClick={onManageRoles}
      sx={{ borderColor: '#c8a96e', color: '#c8a96e', borderRadius: 2, mr: 1, fontSize: 12,
        '&:hover': { borderColor: '#b8935a', backgroundColor: 'rgba(200,169,110,0.08)' } }}>
      ניהול תפקידים
    </Button>
    <CreateButton label="עובד חדש" sx={btn.create} />
  </TopToolbar>
);

// ── Worker table ───────────────────────────────────────────────────────────────
const WorkerTable = () => {
  const { data = [], isLoading } = useListContext();
  const redirect = useRedirect();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { fitCount, renderCount, containerRef, startIndex } = useFitCount({ fallback: 10, page });

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.trim().toLowerCase();
    return data.filter(w =>
      (w.name  || '').toLowerCase().includes(q) ||
      (w.phone || '').includes(q) ||
      (w.role_names || []).some(n => n.toLowerCase().includes(q))
    );
  }, [data, search]);

  useEffect(() => { setPage(1); }, [search]);

  const pageItems = useMemo(() => {
    return filtered.slice(startIndex, startIndex + renderCount);
  }, [filtered, startIndex, renderCount]);

  if (isLoading) return null;
  return (
    <Box>
      <Box px={1} py={1.5}>
        <TextField size="small" placeholder="חיפוש לפי שם, תפקיד או טלפון..." aria-label="חיפוש עובדים"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>, sx: { ...inputSx, direction: 'rtl' } }}
          sx={{ width: 300 }} />
      </Box>
      <TableContainer component={Box} sx={{ ...tbl.container, border: '1px solid rgba(13,29,62,0.06)' }}>
        <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table>
          <TableHead><TableRow>
            <TableCell sx={{ width: 60 }}>#</TableCell>
            <TableCell>שם</TableCell>
            <TableCell>תפקיד</TableCell>
            <TableCell>טלפון</TableCell>
            <TableCell>הערות</TableCell>
          </TableRow></TableHead>
          <TableBody ref={containerRef}>
            {pageItems.map((w, i) => (
                <TableRow key={w.id} onClick={() => redirect('edit', 'workers', w.id)}
                  {...clickableA11y(() => redirect('edit', 'workers', w.id), { ariaLabel: `פתיחת עובד ${w.name || ''}` })}
                  sx={tbl.bodyRow(i)}>
                  <TableCell><Typography fontWeight={600} color="text.secondary" fontSize={13}>#{w.id}</Typography></TableCell>
                  <TableCell><Typography fontWeight={600} fontSize={14}>{w.name}</Typography></TableCell>
                  <TableCell>
                    {(w.role_names || []).length > 0
                      ? <Stack className="crm-wrap" direction="row" spacing={0.5} flexWrap="wrap">
                          {w.role_names.map(n => {
                            const rc = roleColor(n);
                            return <Chip key={n} label={n} size="small" sx={{ backgroundColor: rc.bg, color: rc.color, fontWeight: 600, fontSize: 11 }} />;
                          })}
                        </Stack>
                      : <Typography color="text.secondary" fontSize={13}>—</Typography>}
                  </TableCell>
                  <TableCell><Typography fontSize={13} color="text.secondary">{w.phone || '—'}</Typography></TableCell>
                  <TableCell><Typography fontSize={12} color="text.secondary">{w.notes || '—'}</Typography></TableCell>
                </TableRow>
            ))}

            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>{search ? 'לא נמצאו תוצאות' : 'אין עובדים עדיין'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table></Box>
      </TableContainer>
      <FitPagination page={page} setPage={setPage} total={filtered.length} perPage={fitCount} />
    </Box>
  );
};

// ── Main ───────────────────────────────────────────────────────────────────────
const WorkerList = () => {
  const [rolesOpen, setRolesOpen] = useState(false);
  return (
    <>
      <List className="crm-mobile-list"
        sort={{ field: 'id', order: 'DESC' }}
        perPage={1000}
        pagination={false}
        storeKey={false}
        actions={<Toolbar onManageRoles={() => setRolesOpen(true)} />}
        title="עובדים"
        sx={tbl.listSx}
      >
        <WorkerTable />
      </List>
      <ManageRolesDialog open={rolesOpen} onClose={() => setRolesOpen(false)} />
    </>
  );
};

export default WorkerList;
